import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import ConnString from '../../ConnString.js';

const Connstring2 = ConnString.connstring;

const TLMonitoringTasks = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newTaskReturnType, setNewTaskReturnType] = useState(null);
  const [newTaskPeriod, setNewTaskPeriod] = useState('');
  const [newTaskDeadline, setNewTaskDeadline] = useState(null);
  const [newTaskRemarks, setNewTaskRemarks] = useState('');
  const [newTaskTouched, setNewTaskTouched] = useState(false);
  const [newTaskSaving, setNewTaskSaving] = useState(false);
  const [newTaskError, setNewTaskError] = useState('');
  const [viewTaskOpen, setViewTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [returnTypeOptions, setReturnTypeOptions] = useState([]);
  const [returnTypeLoading, setReturnTypeLoading] = useState(false);
  const [returnTypeError, setReturnTypeError] = useState('');

  const getDefaultMonthBounds = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      from: firstDay,
      to: lastDay,
    };
  };
  const defaultMonthBounds = getDefaultMonthBounds();
  const [dateFrom, setDateFrom] = useState(defaultMonthBounds.from);
  const [dateTo, setDateTo] = useState(defaultMonthBounds.to);
  const [compID] = useState(() => sessionStorage.getItem('compID'));
  const [userID] = useState(() => sessionStorage.getItem('userID'));
  const selectedCompID = compID || sessionStorage.getItem('compID');
  const selectedUserID = userID || sessionStorage.getItem('userID');

  const getCellValue = (row, keys) => {
    for (const key of keys) {
      const value = row?.[key];
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return '—';
  };

  const tasks = useMemo(() => {
    return rows.map((row, index) => ({
      id: row.id ?? row.RecID ?? row.Id ?? index,
      taskName: String(getCellValue(row, ['TaskName', 'Task', 'Name', 'Description', 'Subject'])),
      deadline: String(getCellValue(row, ['Deadline', 'DueDate', 'TaskDate', 'Date'])),
      period: String(getCellValue(row, ['Period', 'PeriodName', 'PeriodCode', 'Month', 'Cycle'])),
      remarks: String(getCellValue(row, ['Remarks', 'Comment', 'Notes'])),
      status: String(getCellValue(row, ['Status', 'TaskStatus'])).trim() || 'Pending',
      owner: String(getCellValue(row, ['AssignedTo', 'Owner', 'UserName'])),
    }));
  }, [rows]);

  const formatDateForDisplay = (date) => {
    if (!date) return '—';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  };

  const formatDateForApi = (date) => {
    if (!date) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  };

  const fetchData = async () => {
    if (!selectedCompID) {
      setRows([]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${Connstring2}/api/MonitoringTasks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: selectedCompID,
          dateFrom: formatDateForApi(dateFrom),
          dateTo: formatDateForApi(dateTo),
        }),
      });

      const data = await response.json();

      if (!data?.success || !Array.isArray(data.tasks)) {
        setRows([]);
        return;
      }

      const transformedRows = data.tasks.map((row, index) => ({
        ...row,
        id: row.id ?? row.RecID ?? row.Id ?? index,
        TaskName: row.TaskName ?? row.Returnx ?? row.Name ?? 'Untitled task',
        Deadline: row.Deadline ?? '—',
        Period: row.Period ?? '—',
        cnt: row.cnt ?? 0,
      }));

      setRows(transformedRows);
    } catch (error) {
      console.error('Error fetching task data:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCompID) {
      setRows([]);
      return;
    }

    fetchData();
  }, [selectedCompID]);

  const fetchReturnTypes = async () => {
    if (!selectedCompID) {
      setReturnTypeOptions([]);
      return;
    }

    setReturnTypeLoading(true);
    setReturnTypeError('');

    try {
      const response = await fetch(`${Connstring2}/api/MonitoringTasks/ReturnTypes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID: selectedCompID }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success || !Array.isArray(data.returnTypes)) {
        setReturnTypeOptions([]);
        setReturnTypeError(data?.message || 'Unable to load Return Types.');
        return;
      }

      const options = data.returnTypes.map((row) => {
        const returnCode = (row.Return || '').toString().trim();
        const description = (row.Descriptions || '').toString().trim();
        const label = description ? `${returnCode} - ${description}` : returnCode;
        return {
          returnCode,
          description,
          label,
        };
      }).filter((item) => item.returnCode);

      setReturnTypeOptions(options);
    } catch (error) {
      console.error('Error fetching return types:', error);
      setReturnTypeOptions([]);
      setReturnTypeError('Unable to load Return Types due to network/server error.');
    } finally {
      setReturnTypeLoading(false);
    }
  };

  const resetNewTaskForm = () => {
    setNewTaskReturnType(null);
    setNewTaskPeriod('');
    setNewTaskDeadline(null);
    setNewTaskRemarks('');
    setNewTaskTouched(false);
    setNewTaskError('');
  };

  const handleOpenNewTask = () => {
    resetNewTaskForm();
    setNewTaskOpen(true);
    fetchReturnTypes();
  };

  const handleCloseNewTask = () => {
    setNewTaskOpen(false);
  };

  const isNewTaskValid = Boolean(newTaskReturnType && newTaskPeriod.trim() && newTaskDeadline);

  const handleOpenViewTask = (task) => {
    setSelectedTask(task);
    setViewTaskOpen(true);
  };

  const handleCloseViewTask = () => {
    setViewTaskOpen(false);
    setSelectedTask(null);
  };

  const handleCreateNewTask = async () => {
    setNewTaskTouched(true);
    if (!isNewTaskValid) {
      return;
    }

    if (!selectedCompID) {
      setNewTaskError('Company is not selected. Please re-login and try again.');
      return;
    }

    setNewTaskSaving(true);
    setNewTaskError('');

    try {
      const response = await fetch(`${Connstring2}/api/MonitoringTasks/Create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: selectedCompID,
          Returnx: newTaskReturnType?.returnCode,
          Period: newTaskPeriod.trim(),
          Deadline: formatDateForApi(newTaskDeadline),
          Remarks: newTaskRemarks.trim(),
          AddedBy: selectedUserID || '',
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        setNewTaskError(data?.message || 'Unable to save task. Please try again.');
        return;
      }

      setNewTaskOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error creating task:', error);
      setNewTaskError('Unable to save task due to network/server error.');
    } finally {
      setNewTaskSaving(false);
    }
  };

  const summaryCards = [
    { label: 'Total Tasks', value: tasks.length, accent: colors.blueAccent?.[500] ?? '#6870fa' },
    { label: 'Pending', value: tasks.filter((task) => task.status.toLowerCase() !== 'posted').length, accent: colors.redAccent?.[400] ?? '#e2726e' },
    { label: 'Due Soon', value: Math.max(0, Math.min(tasks.length, 3)), accent: colors.greenAccent?.[500] ?? '#4cceac' },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      {/* <Header subtitle="Task monitoring" /> */}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 4,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(14,116,144,0.95), rgba(59,130,246,0.9))'
            : 'linear-gradient(135deg, #2563eb, #38bdf8)',
          color: '#fff',
          boxShadow: '0 20px 45px rgba(37, 99, 235, 0.24)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Task Monitoring
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              View every task with its deadline and reporting period in one place.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={fetchData} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenNewTask} sx={{ bgcolor: '#fff', color: '#2563eb', '&:hover': { bgcolor: '#f8fafc' } }}>
              New Task
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Paper key={card.label} elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}` }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{card.label}</Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: card.accent }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Stack> */}

      <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}` }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Task List</Typography>
              <Typography variant="body2" color="text.secondary">Showing task name, deadline, and period.</Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From"
                  value={dateFrom}
                  onChange={(newValue) => setDateFrom(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="To"
                  value={dateTo}
                  onChange={(newValue) => setDateTo(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
              <Button variant="contained" onClick={fetchData} disabled={loading}>
                Apply
              </Button>
              <Chip label={`${tasks.length} items`} color="primary" variant="outlined" />
            </Stack>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600}>No tasks available</Typography>
            <Typography variant="body2" color="text.secondary">The current company has no monitoring tasks to display yet.</Typography>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
            {tasks.map((task) => (
              <Paper key={task.id} variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight={700}>{task.taskName}</Typography>
                      <Chip label={task.status} size="small" color={task.status.toLowerCase() === 'posted' ? 'success' : 'warning'} variant="outlined" />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ color: 'text.secondary' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayOutlinedIcon fontSize="small" />
                        <Typography variant="body2">Deadline: {task.deadline}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleOutlinedIcon fontSize="small" />
                        <Typography variant="body2">Period: {task.period}</Typography>
                      </Box>
                      <Typography variant="body2">Count: {task.owner || task.cnt || 0}</Typography>
                    </Stack>
                    {task.owner && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Owner: {task.owner}
                      </Typography>
                    )}
                    {task.remarks && task.remarks !== '—' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Remarks: {task.remarks}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ borderRadius: 999, px: 2 }}
                    onClick={() => handleOpenViewTask(task)}
                  >
                    View
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      <Dialog open={newTaskOpen} onClose={handleCloseNewTask} fullWidth maxWidth="sm">
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={returnTypeOptions}
              value={newTaskReturnType}
              onChange={(event, newValue) => setNewTaskReturnType(newValue)}
              loading={returnTypeLoading}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option.returnCode === value.returnCode}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Return Type"
                  required
                  error={newTaskTouched && !newTaskReturnType}
                  helperText={
                    returnTypeError ||
                    (newTaskTouched && !newTaskReturnType ? 'Return Type is required' : '')
                  }
                />
              )}
            />

            <TextField
              label="Period"
              value={newTaskPeriod}
              onChange={(event) => setNewTaskPeriod(event.target.value)}
              required
              error={newTaskTouched && !newTaskPeriod.trim()}
              helperText={newTaskTouched && !newTaskPeriod.trim() ? 'Period is required' : ''}
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Deadline"
                value={newTaskDeadline}
                onChange={(newValue) => setNewTaskDeadline(newValue)}
                slotProps={{
                  textField: {
                    required: true,
                    error: newTaskTouched && !newTaskDeadline,
                    helperText: newTaskTouched && !newTaskDeadline ? 'Deadline is required' : '',
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Remarks"
              value={newTaskRemarks}
              onChange={(event) => setNewTaskRemarks(event.target.value)}
              multiline
              minRows={3}
            />

            {newTaskError && (
              <Typography variant="body2" color="error">
                {newTaskError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseNewTask} disabled={newTaskSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateNewTask} disabled={newTaskSaving}>
            {newTaskSaving ? 'Saving...' : 'Save Task'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewTaskOpen} onClose={handleCloseViewTask} fullWidth maxWidth="sm">
        <DialogTitle>Task Details</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2"><strong>Task:</strong> {selectedTask?.taskName || '—'}</Typography>
            <Typography variant="body2"><strong>Deadline:</strong> {selectedTask?.deadline || '—'}</Typography>
            <Typography variant="body2"><strong>Period:</strong> {selectedTask?.period || '—'}</Typography>
            <Typography variant="body2"><strong>Status:</strong> {selectedTask?.status || '—'}</Typography>
            <Typography variant="body2"><strong>Owner:</strong> {selectedTask?.owner || '—'}</Typography>
            <Typography variant="body2"><strong>Remarks:</strong> {selectedTask?.remarks || '—'}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseViewTask}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TLMonitoringTasks;